import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

const VERDICT_STYLES = {
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  candidate: "bg-sky-100 text-sky-800 border-sky-300",
  conflict: "bg-rose-100 text-rose-800 border-rose-300",
  insufficient_evidence: "bg-slate-100 text-slate-700 border-slate-300",
  needs_review: "bg-amber-100 text-amber-800 border-amber-300",
};

const STATUS_STYLES = {
  ok: "bg-emerald-100 text-emerald-800 border-emerald-300",
  error: "bg-rose-100 text-rose-800 border-rose-300",
  provider_unavailable: "bg-slate-100 text-slate-600 border-slate-300",
  insufficient_evidence: "bg-amber-100 text-amber-800 border-amber-300",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || "bg-slate-100 text-slate-700";
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${cls}`}
    >
      {status}
    </span>
  );
}

function VerdictBadge({ verdict }) {
  const cls = VERDICT_STYLES[verdict] || "bg-slate-100 text-slate-700";
  return (
    <span
      data-testid={`verdict-badge-${verdict}`}
      className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${cls}`}
    >
      {verdict}
    </span>
  );
}

function KeyValRow({ k, v }) {
  return (
    <div className="flex justify-between border-b py-1 text-sm">
      <span className="text-slate-500">{k}</span>
      <span className="font-mono text-slate-800 break-all">
        {v === null || v === undefined ? "—" : String(v)}
      </span>
    </div>
  );
}

function ProvidersTable({ results, filterProvider, filterStatus, setFilterProvider, setFilterStatus, providerNames, statusNames }) {
  const rows = results.filter((r) => {
    if (filterProvider !== "*" && r.provider !== filterProvider) return false;
    if (filterStatus !== "*" && r.status !== filterStatus) return false;
    return true;
  });
  return (
    <Card data-testid="providers-table-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Provider eseguiti</CardTitle>
        <div className="flex gap-2">
          <Select value={filterProvider} onValueChange={setFilterProvider}>
            <SelectTrigger className="w-40" data-testid="filter-provider">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="*">Tutti i provider</SelectItem>
              {providerNames.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="*">Tutti gli stati</SelectItem>
              {statusNames.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead>Versione</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Durata (ms)</TableHead>
              <TableHead>Evidenze</TableHead>
              <TableHead>Errore / Impatto</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500">
                Nessun provider corrisponde ai filtri correnti.
              </TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.provider} data-testid={`row-provider-${r.provider}`}>
                <TableCell className="font-medium">{r.provider}</TableCell>
                <TableCell className="font-mono text-xs">{r.provider_version}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell className="font-mono text-xs">
                  {r.execution_time_ms?.toFixed?.(1) ?? "—"}
                </TableCell>
                <TableCell className="font-mono text-xs">{r.evidences?.length ?? 0}</TableCell>
                <TableCell className="text-xs">
                  {r.error_code && <div><b>{r.error_code}</b>: {r.error_message}</div>}
                  {r.impact?.length > 0 && (
                    <div className="text-slate-500 mt-1">
                      Impatto: {r.impact.join(", ")}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function EvidencesTable({ fusionFields, filterField, filterVerdict, setFilterField, setFilterVerdict, fieldNames, onSelectEvidence }) {
  const rows = Object.entries(fusionFields).filter(([key, f]) => {
    if (filterField !== "*" && key !== filterField) return false;
    if (filterVerdict !== "*" && f.verdict !== filterVerdict) return false;
    return true;
  });
  return (
    <Card data-testid="fusion-table-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Evidenze per campo (Evidence Fusion)</CardTitle>
        <div className="flex gap-2">
          <Select value={filterField} onValueChange={setFilterField}>
            <SelectTrigger className="w-56" data-testid="filter-field">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="*">Tutti i campi</SelectItem>
              {fieldNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterVerdict} onValueChange={setFilterVerdict}>
            <SelectTrigger className="w-44" data-testid="filter-verdict">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="*">Tutti i verdetti</SelectItem>
              <SelectItem value="confirmed">confirmed</SelectItem>
              <SelectItem value="candidate">candidate</SelectItem>
              <SelectItem value="conflict">conflict</SelectItem>
              <SelectItem value="insufficient_evidence">insufficient_evidence</SelectItem>
              <SelectItem value="needs_review">needs_review</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="text-center text-slate-500 py-8">
            Nessun campo corrisponde ai filtri correnti.
          </div>
        ) : rows.map(([key, f]) => (
          <div key={key} className="mb-6 border rounded p-3 bg-white" data-testid={`fusion-field-${key}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-medium text-slate-800">{key}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Provider conferma: {(f.providers_ok || []).join(", ") || "—"} · Insufficienti: {(f.providers_insufficient || []).join(", ") || "—"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <VerdictBadge verdict={f.verdict} />
                <div className="text-xs font-mono text-slate-600">
                  conf {(f.chosen_confidence ?? 0).toFixed?.(2)}
                </div>
              </div>
            </div>
            <div className="text-xs mb-2">
              <b>Valore scelto</b> <span className="font-mono">{f.chosen_value === null ? "—" : JSON.stringify(f.chosen_value).slice(0, 200)}</span>
              <span className="text-slate-500"> (via {f.chosen_provider || "—"})</span>
            </div>
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead>Valore</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Pagina</TableHead>
                  <TableHead>bbox_pt</TableHead>
                  <TableHead>Azione</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(f.evidences || []).map((ev, idx) => (
                  <TableRow key={idx} data-testid={`ev-${key}-${idx}`}>
                    <TableCell className="font-mono">{ev.provider}</TableCell>
                    <TableCell className="font-mono max-w-xs truncate">
                      {ev.value_normalized === null ? "—" : JSON.stringify(ev.value_normalized).slice(0, 60)}
                    </TableCell>
                    <TableCell className="font-mono">{(ev.confidence ?? 0).toFixed?.(2)}</TableCell>
                    <TableCell>{ev.page ?? "—"}</TableCell>
                    <TableCell className="font-mono text-[10px]">
                      {ev.bbox_pt ? ev.bbox_pt.map((v) => v.toFixed(1)).join(", ") : "—"}
                    </TableCell>
                    <TableCell>
                      {ev.bbox_pt && ev.page && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`highlight-btn-${key}-${idx}`}
                          onClick={() => onSelectEvidence({ ...ev, field_key: key })}
                        >
                          Evidenzia
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function PdfDiagnostics() {
  const { jobId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const [filterProvider, setFilterProvider] = useState("*");
  const [filterStatus, setFilterStatus] = useState("*");
  const [filterField, setFilterField] = useState("*");
  const [filterVerdict, setFilterVerdict] = useState("*");
  const [selectedEvidence, setSelectedEvidence] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/pdf-jobs/${jobId}/diagnostics`);
        setData(data);
      } catch (e) {
        setErr(e.response?.data?.detail || e.message || "Errore imprevisto");
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId]);

  const download = async () => {
    try {
      const res = await api.get(`/pdf-jobs/${jobId}/diagnostics/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pdf_diagnostics_${jobId}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Diagnostics scaricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const providerNames = useMemo(
    () => (data?.provider_envelope?.results || []).map((r) => r.provider),
    [data],
  );
  const statusNames = useMemo(
    () => Array.from(new Set((data?.provider_envelope?.results || []).map((r) => r.status))),
    [data],
  );
  const fieldNames = useMemo(
    () => Object.keys(data?.evidence_fusion?.fields || {}),
    [data],
  );

  if (loading) return <div className="p-6 text-slate-500" data-testid="diag-loading">Caricamento diagnostica…</div>;
  if (err) return (
    <div className="p-6" data-testid="diag-error">
      <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded">
        <b>Errore:</b> {typeof err === "string" ? err : JSON.stringify(err)}
      </div>
      <Link className="underline text-sm mt-2 block" to="/rfqs">← Torna alle RFQ</Link>
    </div>
  );
  if (!data) return null;

  const meta = data.job_meta;
  const env = data.provider_envelope;
  const fus = data.evidence_fusion;

  return (
    <div className="p-6 space-y-6" data-testid="pdf-diagnostics-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Provider Diagnostics</h1>
          <p className="text-slate-500 text-sm mt-1">
            Analisi delle evidenze dei provider per il job <span className="font-mono">{meta.job_id}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="download-diag-btn" onClick={download}>
            Scarica JSON
          </Button>
          {meta.rfq_id && (
            <Link to={`/rfqs/${meta.rfq_id}`}>
              <Button variant="ghost" data-testid="back-to-rfq-btn">← RFQ</Button>
            </Link>
          )}
        </div>
      </div>

      {/* Job meta */}
      <Card data-testid="job-meta-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <span>Job</span>
            <Badge variant="outline" className="font-mono text-xs">{meta.schema_version}</Badge>
            {meta.insufficient_data && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="insufficient-badge">
                Dati insufficienti
              </Badge>
            )}
            {meta.reliability && (
              <Badge variant="outline">Reliability: {meta.reliability}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
          <KeyValRow k="Job ID" v={meta.job_id} />
          <KeyValRow k="Tenant" v={meta.tenant_id} />
          <KeyValRow k="File ID" v={meta.file_id} />
          <KeyValRow k="File hash" v={meta.file_hash?.slice?.(0, 32) + "…"} />
          <KeyValRow k="Schema version" v={meta.schema_version} />
          <KeyValRow k="Creato" v={meta.created_at} />
          <KeyValRow k="Classificazione" v={meta.classification} />
          <KeyValRow k="Geometry status" v={meta.geometry_status} />
          <KeyValRow k="Vision AI usato" v={meta.used_vision ? "sì" : "no"} />
          <KeyValRow k="Scale nota" v={meta.scale_known ? "sì" : "no"} />
          <KeyValRow k="Area (pdf_points)" v={meta.area_pdf_points} />
          <KeyValRow k="Area (mm²)" v={meta.manufacturing_area_mm2} />
          <KeyValRow k="Bend count" v={meta.bend_count} />
          <KeyValRow k="Evidenza pieghe geometrica" v={meta.geometric_bend_evidence ? "sì" : "no"} />
          <KeyValRow k="Pieghe geometriche" v={meta.n_bends_geometric} />
          <KeyValRow k="Pieghe da testo" v={meta.n_bends_text_only} />
        </CardContent>
      </Card>

      {/* Summary counts */}
      <Card>
        <CardHeader><CardTitle>Riepilogo Evidence Fusion</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          {["confirmed", "candidate", "conflict", "insufficient_evidence", "needs_review"].map((v) => (
            <div key={v} className="flex items-center gap-2 border rounded px-3 py-1"
                    data-testid={`counts-${v}`}>
              <VerdictBadge verdict={v} />
              <span className="font-mono text-sm">{fus.counts?.[v] ?? 0}</span>
            </div>
          ))}
          <div className="flex items-center gap-2 border rounded px-3 py-1 bg-slate-50"
                data-testid="counts-total">
            <span className="text-slate-600 text-xs">Totale campi</span>
            <span className="font-mono text-sm">{fus.n_fields ?? 0}</span>
          </div>
          <div className="flex items-center gap-2 border rounded px-3 py-1 bg-slate-50"
                data-testid="counts-providers">
            <span className="text-slate-600 text-xs">Provider ok / err / unavailable</span>
            <span className="font-mono text-sm">
              {env.summary?.ok ?? 0} / {env.summary?.error ?? 0} / {env.summary?.unavailable ?? 0}
            </span>
          </div>
        </CardContent>
      </Card>

      <ProvidersTable
        results={env.results || []}
        filterProvider={filterProvider} setFilterProvider={setFilterProvider}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        providerNames={providerNames} statusNames={statusNames}
      />

      <EvidencesTable
        fusionFields={fus.fields || {}}
        filterField={filterField} setFilterField={setFilterField}
        filterVerdict={filterVerdict} setFilterVerdict={setFilterVerdict}
        fieldNames={fieldNames}
        onSelectEvidence={(ev) => setSelectedEvidence(ev)}
      />

      {selectedEvidence && (
        <Card data-testid="selected-evidence-panel">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Evidenza selezionata (pagina {selectedEvidence.page})</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setSelectedEvidence(null)}>
              Chiudi
            </Button>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-slate-600 mb-2">
              <b>Campo:</b> <code>{selectedEvidence.field_key}</code> ·{" "}
              <b>Provider:</b> <code>{selectedEvidence.provider}</code> ·{" "}
              <b>bbox_pt:</b> <code>{selectedEvidence.bbox_pt?.map((v) => v.toFixed(1)).join(", ")}</code>
            </div>
            <PdfPagePreview
              jobId={jobId}
              page={selectedEvidence.page}
              bbox={selectedEvidence.bbox_pt}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PdfPagePreview({ jobId, page, bbox }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [dims, setDims] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/pdf-jobs/${jobId}/page/${page}?dpi=140`, {
          responseType: "blob",
        });
        if (!alive) return;
        const url = URL.createObjectURL(res.data);
        setImgUrl(url);
      } catch (e) {
        // preview is optional
      }
    })();
    return () => { alive = false; };
  }, [jobId, page]);
  if (!imgUrl) return <div className="text-slate-500 text-sm">Anteprima non disponibile</div>;
  const DPI = 140;
  const [x0, y0, x1, y1] = (bbox || [0, 0, 0, 0]);
  const scale = DPI / 72;
  const overlayStyle = dims ? {
    position: "absolute",
    left: x0 * scale,
    top: y0 * scale,
    width: (x1 - x0) * scale,
    height: (y1 - y0) * scale,
    border: "2px solid #f97316",
    boxShadow: "0 0 0 2px rgba(249,115,22,0.25)",
    pointerEvents: "none",
  } : { display: "none" };
  return (
    <div className="relative border inline-block max-w-full overflow-auto"
          data-testid="pdf-page-preview">
      <img
        src={imgUrl}
        alt={`Pagina ${page}`}
        onLoad={(e) => setDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
        style={{ display: "block", maxWidth: "100%" }}
      />
      <div style={overlayStyle} data-testid="pdf-bbox-overlay" />
    </div>
  );
}
